import { FC, useCallback, useMemo } from 'react';
import { Box, Stack } from '@mui/material';
import {
  ConfigureConnectionInput,
  Connection,
  ConnectionFormFields,
} from '../types/connection-types';
import DoubleActionButtonGroup from '../../common/components/DoubleActionButtonGroup';
import {
  removeConnectionTooltip,
  saveConnectionTooltip,
} from '../utils/button-tooltip-utils';

const SaveRemoveButtonGroup: FC<
  React.PropsWithChildren<SaveRemoveButtonGroupProps>
> = props => {
  const {
    connection,
    connectionFormFields,
    onSaveConnection,
    onRemoveConnection,
    isReadOnlyRole,
    isUpdatable,
    isLoadingSave,
    isLoadingRemove,
    isSelfConnection,
  } = props;

  const configureConnectionInput = useMemo(() => {
    return { connectionId: connection.id } as ConfigureConnectionInput;
  }, [connection]);

  const handleOnSaveConnection = useCallback(() => {
    onSaveConnection(connection.id, connectionFormFields);
  }, [onSaveConnection, connection, connectionFormFields]);

  const handleOnRemoveConnection = useCallback(() => {
    onRemoveConnection(configureConnectionInput);
  }, [onRemoveConnection, configureConnectionInput]);

  const isConnectionEnabled = connection.enabled;
  const isRemoveConnectionDisabled =
    !isConnectionEnabled || isSelfConnection || isReadOnlyRole;
  const isSaveConnectionDisabled = !isUpdatable || isReadOnlyRole;

  const saveConnectionTooltipTitle = saveConnectionTooltip(isReadOnlyRole);
  const removeConnectionTooltipTitle = removeConnectionTooltip(
    isReadOnlyRole,
    isSelfConnection,
  );

  return (
    <Box display='flex' flexDirection='column' gap={3} mt={1}>
      <Stack
        gap={2}
        width='100%'
        justifyContent='flex-end'
        direction='row'
        alignItems='center'>
        <DoubleActionButtonGroup
          onPrimaryClick={handleOnSaveConnection}
          onSecondaryClick={handleOnRemoveConnection}
          isPrimaryLoading={isLoadingSave}
          isSecondaryLoading={isLoadingRemove}
          primaryButtonActionLabel={'Save'}
          primaryButtonActionTooltip={saveConnectionTooltipTitle}
          primaryButtonActionColor={'success'}
          secondaryButtonActionLabel={'Revoke'}
          secondaryButtonActionTooltip={removeConnectionTooltipTitle}
          secondaryButtonActionColor={'error'}
          isPrimaryActionDisabled={isSaveConnectionDisabled}
          isSecondaryActionDisabled={isRemoveConnectionDisabled}
        />
      </Stack>
    </Box>
  );
};

export interface SaveRemoveButtonGroupProps {
  connection: Connection;
  connectionFormFields: ConnectionFormFields;
  onSaveConnection: (
    connectionId: string,
    connectionInput: ConnectionFormFields,
  ) => void;
  onRemoveConnection: (
    configureConnectionInput: ConfigureConnectionInput,
  ) => void;
  isReadOnlyRole: boolean;
  isUpdatable: boolean;
  isLoadingSave: boolean;
  isLoadingRemove: boolean;
  isSelfConnection: boolean;
}

export default SaveRemoveButtonGroup;
